import { EMessengerScreenName } from '@xometry/ui';
import { isEqual, omit } from 'es-toolkit/compat';
import { toJS } from 'mobx';
import React from 'react';

import { MicroFrontend } from './MicroFrontend';
import type { MicroFrontendProps } from './MicroFrontend.types';
import { MICRO_FRONTEND_NAME } from './MicroFrontend.types';

/**
 * Chat identity only — titles, subscription counters, etc. change on every MobX/Apollo tick on the deal page and
 * must not force `MicroFrontend` to re-run `renderMessenger`.
 */
const stableChatSourceForEquality = (chatSource: Record<string, unknown>): Record<string, unknown> => {
  const parent = chatSource.parent;

  const stableParent =
    parent && typeof parent === 'object'
      ? {
          dealId: (parent as Record<string, unknown>).dealId,
          sourceId: (parent as Record<string, unknown>).sourceId,
          sourceType: (parent as Record<string, unknown>).sourceType,
        }
      : undefined;

  return {
    id: chatSource.id,
    sourceId: chatSource.sourceId,
    sourceType: chatSource.sourceType,
    dealId: chatSource.dealId,
    ...(stableParent ? { parent: stableParent } : {}),
  };
};

const normalizeInitialScreenForMessengerEquality = (initialScreen: unknown): unknown => {
  if (!initialScreen || typeof initialScreen !== 'object') return initialScreen;

  const screen = initialScreen as Record<string, unknown>;

  if (screen.name !== EMessengerScreenName.CHAT || !screen.params || typeof screen.params !== 'object') {
    return initialScreen;
  }

  const params = screen.params as Record<string, unknown>;
  const chatSource = params.chatSource;

  if (!chatSource || typeof chatSource !== 'object') return initialScreen;

  return {
    ...screen,
    params: {
      ...params,
      chatSource: stableChatSourceForEquality(chatSource as Record<string, unknown>),
    },
  };
};

/**
 * Data-only snapshot for equality. Strips **unstable callback identities** so `isEqual` compares only serializable
 * props; `setEventBusRef`, `amplitudeLogger`, and `onRequestClassicLayout` are compared **by reference** separately
 * in {@link areMessengerAppParamsEquivalent} (host recreates them every render — deep equality would always fail).
 */
const messengerAppParamsDataSnapshot = (params: unknown): unknown => {
  if (!params || typeof params !== 'object') return params;

  const plain = toJS(params) as Record<string, unknown>;
  const rest = omit(plain, ['setEventBusRef', 'amplitudeLogger', 'onRequestClassicLayout']) as Record<string, unknown>;

  if (!rest.initialScreen) return rest;

  return {
    ...rest,
    initialScreen: normalizeInitialScreenForMessengerEquality(rest.initialScreen),
  };
};

/** True when messenger should not call `renderMessenger` again (avoids MFE churn on MobX-driven parent re-renders). */
const areMessengerAppParamsEquivalent = (prev: unknown, next: unknown): boolean => {
  const p = prev as Record<string, unknown>;
  const n = next as Record<string, unknown>;

  return (
    isEqual(messengerAppParamsDataSnapshot(prev), messengerAppParamsDataSnapshot(next)) &&
    p.setEventBusRef === n.setEventBusRef &&
    p.amplitudeLogger === n.amplitudeLogger &&
    p.onRequestClassicLayout === n.onRequestClassicLayout
  );
};

const areMessengerMicroFrontendPropsEqual = (prev: MicroFrontendProps, next: MicroFrontendProps): boolean => {
  if (prev.name !== MICRO_FRONTEND_NAME.Messenger || next.name !== MICRO_FRONTEND_NAME.Messenger) return false;

  return (
    prev.containerId === next.containerId &&
    prev.host === next.host &&
    prev.onRender === next.onRender &&
    areMessengerAppParamsEquivalent(prev.appParams, next.appParams)
  );
};

/**
 * Same as {@link MicroFrontend} for `name === Messenger`, but skips React re-renders (and the inner MF effect) when
 * only the host re-rendered with a fresh `appParams` object reference — MobX/Apollo on the deal page does this often.
 */
export const ErpMessengerMicroFrontend = React.memo(MicroFrontend, areMessengerMicroFrontendPropsEqual);
ErpMessengerMicroFrontend.displayName = 'ErpMessengerMicroFrontend';
